import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {MatDialog} from '@angular/material/dialog';
import {Product} from "../../shared/models/product";
import {NGXLogger} from "ngx-logger";
import {ProductControllerService as FalconProductControllerService} from "falcon-api";
import {ApplicationService} from "../application.service";
import {
  ApplicationLogDetailsRequest,
  ApplicationLogsControllerService,
  ApplicationLogStepRequest,
  Log,
  Step,
  ApplicationProduct, ApplicationProductRequest
} from "leia-api";
import {MatSnackBar} from "@angular/material/snack-bar";
import {Router} from "@angular/router";
import {UtilitiesService} from "../../core/services/utilities.service";
import {
  CustomerControllerService,
  ProductControllerService
} from 'skyhopper-api';
import {Application} from 'src/app/shared/models/application';
// models
//Service
import {FacadeService} from 'src/app/shared/services/facade.service';
import {Subject} from "rxjs";
import {MatRadioChange} from "@angular/material/radio";
import {takeUntil} from "rxjs/operators";
import {TellUsAboutYourselfData} from "../../shared/models/form-data";
import {OnboardingApplicant} from "../../shared/models/onboarding-applicant.model";
import {FormState} from "../../core/interfaces/form-state";
import {MatTabGroup} from "@angular/material/tabs";
import {ValidationDetail} from "../../shared/models/ValidationDetail";
import {CountryListValidator} from "./bsa-profile/country-list-validator";
import {CreateApplicationService} from "./create-application.service";
import {ApplicantsService} from "./applicants.service";
import {ApplicantValidator} from "./applicants.validator";
import {
  ADULT_APPLICANT_LABEL,
  EMAIL_APPLICANT_ERROR_KEY,
  FSR_RM_ASSISTED,
  MINOR_APPLICANT_LABEL,
  PHONE_APPLICANT_ERROR_KEY,
  PRIMARY_APPLICANT_LABEL,
  PRODUCT_MINOR_SAVINGS,
  SECONDARY_APPLICANT_LABEL,
  SELECTED_PRODUCT_CODE,
  SSN_APPLICANT_ERROR_KEY,
  SUPPRESS_ACCOUNT_DETAILS,
  SUPPRESS_ACCOUNT_DETAILS_VALUE
} from "../../shared/constants";
import NameEnum = Step.NameEnum;
import StateEnum = Step.StateEnum;
import {ProductType} from "../../shared/models/product-type";
import {ApplicationType} from "../../shared/models/application-type.enum";
import OnboardingStepEnum = ApplicationLogStepRequest.OnboardingStepEnum;
import OnboardingStateEnum = ApplicationLogStepRequest.OnboardingStateEnum;
import OnboardingSourceEnum = ApplicationLogDetailsRequest.OnboardingSourceEnum;
import {SettingService} from "../../shared/services/setting.service";
import {environment} from "../../../environments/environment";
import {BrandEnum} from "../../shared/models/brand.enum";
import {SuccessDialogComponent} from "../../shared/components/success-dialog/success-dialog.component";

@Component({
  selector: 'app-create',
  templateUrl: './create-application.component.html',
  styleUrls: ['./create-application.component.scss']
})
export class CreateApplicationComponent implements OnInit, OnDestroy {
  @ViewChild('countryInput') countryInput!: ElementRef;
  @ViewChild(MatTabGroup) applicantTabs!: MatTabGroup;
  jointApplicant: boolean = false;

  products: Array<Product> = new Array<Product>();
  selectedProduct?: Product;
  application: Application;
  applicants: OnboardingApplicant[] = [];
  productErrors: Array<string> = [];
  shouldHideBsaQuestions: boolean = true;
  hideSecondaryApplicant = true;
  destroySubject$: Subject<void> = new Subject();
  shouldDisplayOrderCheckQues = true;
  shouldDisplayOrderDebitQues = true;
  // Form Validation
  appForm = new FormGroup({
    applicants: new FormArray([]),
    internalDetailsFormGroup: new FormGroup({
      costCenter: new FormControl({ value: '', disabled: true }),
      officerCode: new FormControl(''),
    })
  }, {
    validators: [this.applicantValidator.uniqueData()]
  });
  validationDetail: ValidationDetail = new ValidationDetail(this.appForm, new Map());
  sendPrimaryTellUsAboutYourselfData!: () => TellUsAboutYourselfData;
  hideJointApplicantCheckField: boolean = false;
  applicantTabLabelMap: Map<number, string> = new Map<number, string>();
  isProductLoading: boolean = false;
  disableCreateButton = false;

  constructor(private formBuilder: FormBuilder,
              private falconProductService: FalconProductControllerService,
              private applicationService: ApplicationService,
              private snackBar: MatSnackBar,
              private router: Router,
              private logger: NGXLogger,
              private customerControllerService: CustomerControllerService,
              private utils: UtilitiesService,
              public dialog: MatDialog,
              private facadeService: FacadeService,
              private skyhopperProductService: ProductControllerService,
              private leiaService: ApplicationLogsControllerService,
              private bsaCountryListValidator: CountryListValidator,
              private createApplicationService: CreateApplicationService,
              private applicantsService: ApplicantsService,
              private applicantValidator: ApplicantValidator,
              private settingService: SettingService) {
    this.application = new Application();

    this.facadeService.setPageTitleContext("Create Application");
  }

  get applicantGroups(): FormGroup[] {
    return (<FormArray>this.appForm.get('applicants')).controls as FormGroup[];
  }

  get getInternalDetailsDataForm(): FormGroup {
    return this.appForm.get('internalDetailsFormGroup') as FormGroup;
  }

  ngOnInit(): void {
    this.router.onSameUrlNavigation = 'ignore';
    this.falconProductService.getProducts().subscribe(productDetails => {
      productDetails.forEach(productDetail => {
        if (productDetail.enabled && (productDetail.brand?.id === BrandEnum.TcbPrivate)) {
          let product = Product.mapFromDto(productDetail);
          this.products.push(product);
        }
      });

      // sort by product code
      this.products.sort((product1, product2) => product2.code > product1.code ? -1 : 1);

      //create new session
      this.createNewApplication(() => {
        this._setupValidationMap();
        this._setupStatusTracker();
        this.applicationService.onboardingRequest$
          .pipe(
            takeUntil(this.destroySubject$)
          ).subscribe(() => {
          this.loadApplicantsFormGroup();
        });
        this.createApplicantFormGroup(true, false).then(form => {
          (this.appForm.get('applicants') as FormArray).push(form);
        });
        if (this.jointApplicant) {
          this.appendSecondaryApplicant();
        }
        this.sendPrimaryTellUsAboutYourselfData = this._getPrimaryTellUsAboutYourselfData.bind(this);
      });
    });
  }

  ngOnDestroy(): void {
    this.destroySubject$.next();
  }

  findFilterProducts(product: Product[], filterVal: string): any[] {
    return this.products.filter(product => product.brandName === filterVal);
  }

  async createApplicantFormGroup(isPrimary: boolean, isMinor: boolean): Promise<FormGroup> {
    const tellUsAboutYourselfForm = await this.applicantsService.createTellUsAboutYourselfFormGroup(isPrimary, isMinor);
    const applicantId = tellUsAboutYourselfForm.value.id;
    const applicantForm = this.formBuilder.group({
      tellUsAboutYourself: tellUsAboutYourselfForm,
      employmentDetails: this.applicantsService.createEmploymentDetailsFormGroup(applicantId, isPrimary, isMinor)
    });
    tellUsAboutYourselfForm.statusChanges.subscribe(
      this.onTellUsAboutYourselfFormChanges.bind(this, tellUsAboutYourselfForm)
    );

    const employmentDetailsForm = applicantForm.get("employmentDetails") as FormGroup;
    employmentDetailsForm.statusChanges.subscribe(
      this.onEmploymentDetailsFormChanges.bind(this, employmentDetailsForm)
    );
    return applicantForm;
  }

  /**
   * Create new application, instantiate new ApplicationLogRequestObject.
   */
  createNewApplication(callback: () => void) {
    // clear out the local storage - clean house
    let appliedFilters = this.facadeService.getStorageItem('appliedFilters');
    this.applicationService.clearStorage();
    this.applicationService.id = '';
    const applicationLogRequest = this.applicationService.applicationLogsRequest;

    applicationLogRequest.status = 'Created';
    applicationLogRequest.applicationChannelId = FSR_RM_ASSISTED;
    applicationLogRequest.applicationType = ApplicationType.NewClientApplication;
    applicationLogRequest.isJoint = 0;
    applicationLogRequest.step = this.getLogStep('Coruscant', 'Application Started', true);

    this.applicationService.applicationLogsRequest = applicationLogRequest;
    //this.facadeService.addStorageItem('appliedFilters',appliedFilters);
    callback();

  }

  loadApplicantsFormGroup(): void {
    const applicationLogsRequest = this.applicationService.applicationLogsRequest;
    const applicantsFormArray = (this.appForm.get('applicants') as FormArray);

    if (!applicationLogsRequest.applicants) {
      return;
    }
    applicationLogsRequest.applicants.forEach(applicant => {
      const tellUsAboutYourselfForm = this.applicantsService.loadTellUsAboutYourselfFormGroup(applicant, false);
      const employmentDetailsForm = this.applicantsService.loadEmploymentDetailsFormGroup(applicant, false);

      applicantsFormArray.push(
        this.formBuilder.group({
          tellUsAboutYourself: tellUsAboutYourselfForm,
          employmentDetails: employmentDetailsForm
        })
      );

      if (applicantsFormArray.length > 1) {
        this.hideSecondaryApplicant = false;
        //set the joint applicant radio true.
        this.jointApplicant = true;
      }
      tellUsAboutYourselfForm.statusChanges.subscribe(
        this.onTellUsAboutYourselfFormChanges.bind(this, tellUsAboutYourselfForm)
      );

      employmentDetailsForm.statusChanges.subscribe(
        this.onEmploymentDetailsFormChanges.bind(this, employmentDetailsForm)
      );
    });

  }

  onEmploymentDetailsFormChanges(form: FormGroup, formChanges: string): void {
    if (formChanges === FormState.VALID) {
      this.applicantsService.onValidEmploymentDetails(form);
    }

    if (formChanges === FormState.INVALID) {
      this.applicantsService.onInvalidEmploymentDetails(form);
    }
  }

  onTellUsAboutYourselfFormChanges(form: FormGroup, formChanges: string): void {
    if (formChanges === FormState.VALID) {
      this.applicantsService.onValidTellUsAboutYourself(form);
    }

    if (formChanges === FormState.INVALID) {
      this.applicantsService.onInvalidTellUsAboutYourself(form);
    }
  }

  /**
   * On product change, set product details to application log object
   * @param $event
   */
  onProductChanged(event: any) {
    this.isProductLoading = true;

    let applicationRequest = this.applicationService.applicationLogsRequest;
    let step = this.utils.createStep(NameEnum.ProductSelection, StateEnum.Success, []);

    this.selectedProduct = this.products.find(product => product.code == event.value);

    if (!this.selectedProduct) {
      this.isProductLoading = false;
      return;
    }

    this.facadeService.addStorageItem(SELECTED_PRODUCT_CODE, this.selectedProduct.code + '');

    // US447622 Changes, Get the brand product settings
    // hide bsa question if the config-suppressAccountDetails value is true
    this.settingService
      .getBrandSettings(environment.app_settings_version, this.selectedProduct.brandId, this.selectedProduct.productUID)
      .then(settings => {
        if (settings.length === 0) {
          this.shouldHideBsaQuestions = this.selectedProduct!.productType !== ProductType.Checking;
        }

        settings.forEach(brandProductSetting => {
          const {name, value} = brandProductSetting;
          if (!name && !value) {
            return;
          }

          //get product config settings for Checking accounts.
          //If the value is not empty & false, don't display sub question.
          //If the value is empty or value is true from config setting, display sub question.
          if (name === "orderCheck" && value?.toLowerCase() === 'false') this.shouldDisplayOrderCheckQues = false;
          if (name === "orderDebit" && value?.toLowerCase() === 'false') this.shouldDisplayOrderDebitQues = false;

          this.shouldHideBsaQuestions = this.selectedProduct!.productType !== ProductType.Checking;

          if (name === SUPPRESS_ACCOUNT_DETAILS && value?.toLowerCase() === SUPPRESS_ACCOUNT_DETAILS_VALUE) {
            this.shouldHideBsaQuestions = true;
          }
        });
      });

    //create product array
    let applicationProductRequest = new ApplicationProduct();
    applicationProductRequest.productCode = String(this.selectedProduct.code);
    applicationProductRequest.productDescription = this.selectedProduct.name;
    applicationProductRequest.accountType = this.selectedProduct.accountType;
    applicationProductRequest.productType = this.selectedProduct.productType;
    applicationRequest.products = new Array<ApplicationProductRequest>(applicationProductRequest);
    step.logs!.push(this.utils.createLog("Coruscant", `Product: ${this.selectedProduct.code} - ${this.selectedProduct.name}, Brand: ${this.selectedProduct.brandName}`))
    applicationRequest.step = step;

    applicationRequest.applicationLogStepRequest = this.utils.createApplicationLogStepRequest(OnboardingStepEnum.PRODUCTIONSELECTION, OnboardingStateEnum.SUCCESS, OnboardingSourceEnum.CORUSCANT, [this.utils.createApplicationLogDetailsRequest(OnboardingSourceEnum.CORUSCANT, `Product: ${this.selectedProduct.code} - ${this.selectedProduct.name}, Brand: ${this.selectedProduct.brandName}`)]);

    this.productErrors = [];
    this.applicationService.applicationLogsRequest = applicationRequest;
    this.setFormLabelAndValidator(this.selectedProduct.name);
  }

  /**
   * Change the form label name based on product selection
   * Reset the validator for Minor savings product
   * @param selectedProductName
   */
  setFormLabelAndValidator(selectedProductName: string) {
    const formArray = this.appForm.get('applicants') as FormArray;
    if (selectedProductName === PRODUCT_MINOR_SAVINGS) {
      this.appendSecondaryApplicant();
      this.hideJointApplicantCheckField = true;
      this.applicantTabLabelMap.set(0, MINOR_APPLICANT_LABEL);
      this.applicantTabLabelMap.set(1, ADULT_APPLICANT_LABEL);
      this.applicantsService.updateFormValidators(formArray, true);

    } else {
      if (this.hideJointApplicantCheckField) {
        this.removeSecondaryApplicant();
        this.applicantTabLabelMap.set(0, PRIMARY_APPLICANT_LABEL);
        this.applicantTabLabelMap.set(1, SECONDARY_APPLICANT_LABEL);
        this.hideJointApplicantCheckField = false;
        this.jointApplicant = false;
        this.applicantsService.updateFormValidators(formArray, false);
      }
    }
  }

  multipleApplicantChange(event: MatRadioChange): void {
    if (event.value) {
      this.appendSecondaryApplicant();
    } else {
      this.removeSecondaryApplicant();
    }
  }

  appendSecondaryApplicant(): void {
    const formArray = this.appForm.get("applicants") as FormArray;
    if (formArray.length === 1) {
      this.createApplicantFormGroup(false, this.applicationService.getIsMinor(String(this.selectedProduct?.code))).then(form => {
        formArray.push(form);
      });
    }

    this.hideSecondaryApplicant = false;
    this.applicantsService.unmarkSecondaryApplicantForRemoval();
  }

  removeSecondaryApplicant(): void {
    this.hideSecondaryApplicant = true;
    this.applicantsService.markSecondaryApplicantForRemoval();
    //Fix to remove the appended applicant on application type selection
    this.applicantsService.removeSecondaryApplicantIfNecessary(this.appForm.get('applicants') as FormArray);
  }

  onCreateApplication($event: MouseEvent) {
    if (this.appForm.valid && this.bsaProfileValidationCheck()) {
      this.disableCreateButton = true;

      this.validateProductSelection();
      if (this.productErrors.length > 0) {
        this.utils.scrollToElement("content");
        this.disableCreateButton = false;
        return;
      }
      let applicationRequest = this.applicationService.applicationLogsRequest;


      applicationRequest.products?.forEach(product => {
        product.productUID = this.selectedProduct?.productUID;
      });

      applicationRequest.applicants?.forEach(value => {
        value.applicantId = undefined;
      });
      const {costCenter, officerCode} = this.getInternalDetailsDataForm.controls;
      applicationRequest.costCenter = costCenter.value;
      applicationRequest.officerCode = officerCode.value;
      this.applicationService.applicationLogsRequest = applicationRequest;
      this.applicationService.submitApplication(this.selectedProduct!.brandId, this.onSuccess, this.onError);
    } else {
      this.createApplicationService.triggerValidation();
      this.utils.scrollToElement("content");
      this.disableCreateButton = false;
    }
  }

  bsaProfileValidationCheck(): boolean {
    let bsaFormValid = true;
    if (!this.shouldHideBsaQuestions) {
      if (this.applicationService.bsaProfileStatus) {
        bsaFormValid = true;
      } else {
        bsaFormValid = false;
      }
    }
    return bsaFormValid;
  }

  validateProductSelection() {
    this.productErrors = [];
    if (!this.selectedProduct || this.selectedProduct.code === 0)
      this.productErrors.push("Products");
  }

  getApplicantType(formGroup: FormGroup, index: number): any {
    let applicantType = (formGroup.get("tellUsAboutYourself") as FormGroup).get('applicantType')?.value;
    if (this.applicantTabLabelMap.get(index) === undefined) return applicantType;
    return this.applicantTabLabelMap.get(index);
  }

  getTellUsAboutYourselfFormGroup(applicantFormGroup: FormGroup): FormGroup {
    return applicantFormGroup.get('tellUsAboutYourself') as FormGroup;
  }

  getEmploymentDetailsFormGroup(applicantFormGroup: FormGroup): FormGroup {
    return applicantFormGroup.get('employmentDetails') as FormGroup;
  }

  onSuccess = (sessionId: string, message: string, uniqueLink: string, uniqueCode: string) => {

    //Open dialog if the uniqueLink is not null/empty.
    if (uniqueLink === '') {
      let snackbarRef = this.snackBar.open(message, "View");
      snackbarRef.onAction().subscribe(() => {
        this.router.navigate([`/applications/${sessionId}`]);
      })
    } else {
      this.openDialog(uniqueLink, uniqueCode, sessionId);
    }
  }

  onError = () => {
    let snackbarRef = this.snackBar.open("Could not create application", "Dismiss");
    snackbarRef.onAction().subscribe(() => {
      this.snackBar.dismiss();
    })
  }

  //To show Unique Link & Code.
  openDialog(uniqueLink: string, uniqueCode: string, sessionId: string): void {
    let message = "Application Created";
    let showViewApplicationButton = true;

    const dialogRef = this.dialog.open(SuccessDialogComponent, {
      data: {message, uniqueLink, uniqueCode, sessionId, showViewApplicationButton},
      maxHeight: "100%",
      maxWidth: "100%",
      disableClose: true,
      hasBackdrop: true
    });
  }

  getLogStep(source: string, details: string, isEmptyStep: boolean) {
    let log = this.utils.createLog(source, details);
    let step = isEmptyStep ? this.utils.createEmptyStepForLogging([log]) : this.utils.createEmptyStepForLogging([log]);
    return step;
  }

  private _getPrimaryTellUsAboutYourselfData(): TellUsAboutYourselfData {
    return this.appForm.value.applicants.find((applicant: any) =>
      applicant.tellUsAboutYourself.isPrimary
    )!.tellUsAboutYourself;
  }

  private _setupValidationMap(): void {
    const labelMap = new Map<string, string>();
    labelMap.set(SSN_APPLICANT_ERROR_KEY, "Applicants cannot share the same Social Security Number");
    labelMap.set(EMAIL_APPLICANT_ERROR_KEY, "Applicants cannot share the same Email");
    labelMap.set(PHONE_APPLICANT_ERROR_KEY, "Applicants cannot share the same Phone Number");
    this.validationDetail = new ValidationDetail(this.appForm, labelMap);
  }

  private _setupStatusTracker(): void {
    this.appForm.statusChanges.subscribe(state => {
      const validTellUsAboutYourself: string[] = [];
      const validEmploymentDetails: string[] = [];
      let dirtyTellUsAboutYourself = false;
      let dirtyEmploymentDetails = false;

      (this.appForm.controls.applicants as FormArray).controls.forEach(applicantForm => {
        const {tellUsAboutYourself, employmentDetails} = (applicantForm as FormGroup).controls;

        validTellUsAboutYourself.push(tellUsAboutYourself.status);
        validEmploymentDetails.push(employmentDetails.status);

        if (!dirtyTellUsAboutYourself) {
          dirtyTellUsAboutYourself = tellUsAboutYourself.dirty;
        }
        if (!dirtyEmploymentDetails) {
          dirtyEmploymentDetails = employmentDetails.dirty;
        }
      });

      this.createApplicationService.validationsChange.emit(this.validationDetail);
      this.applicantsService.sendDuplicatedDataErrors(this.appForm.errors!);
    });
  }
}
